import { apiHeaders, axiosRetryGet, axiosRetryPost, axiosRetryPut, localApiInstance } from "../../../utilities/AxiosRetryUtil";
import { IDocumentSetCreationData } from "../interfaces/IDocumentSetCreationData";
import { IDownloadRequest } from "../interfaces/IDownloadRequest";
import { IFileSystemUploadData } from "../interfaces/IFileSystemUploadData";
import { IStoreItem } from "../interfaces/IStoredItem";
import { ITenantInfo } from "../interfaces/ITenantInfo";
import { IUserProfile, IUserSettings } from "../interfaces/IUserProfile";
import { IDocumentSearchRequest } from "../interfaces/advancedSearch/IDocumentSearchRequest";
import { IDocumentSearchResult } from "../interfaces/advancedSearch/IDocumentSearchResult";
import LZString = require("lz-string");
import { IOneDriveDataDto } from "../interfaces/IOneDriveDataDto";
import { IOneDriveOfficeDataDto } from "../interfaces/IOneDriveOfficeDataDto";
import { ISharepointDataDto } from "../interfaces/ISharepointDataDto";
import { ISharepointOfficeDataDto } from "../interfaces/ISharepointOfficeDataDto";
import { IDriveItemRequest } from "../interfaces/IDriveItemRequest";
import axios, { AxiosRequestConfig, AxiosResponse } from "axios";
import { IFilePreCommitRequest } from "../interfaces/IFilePreCommitRequest";
import { ApprovalRequest, BulkMetadataUpdateRequest, ConvertAndSaveAsPDFRequest, CreateDocumentFromTemplateRequest, ExchangeMailFolderUploadRequest, FolderPreCommitRequest, IHookRequest, IHookResult, MetadataUpdateRequest, ReplaceFileRequest, SignRequest, UpdateTriggerRequest } from "../components/_v2/redux/api/local/api-local-interfaces";
import { IAuthAccount, IAuthTokenResultDto, IJwtAuthTokenResult } from "../interfaces/IAuthTokenResultDto";
import { IFileSystemFolder } from "../../../utilities/DragAndDropHelper";
import { IMailSettingsDto } from "../../../utilities/OutlookUtils";
import { UserLicenseTypeEnum } from "../components/enums/userLicenseTypeEnum";
import { IFilePreCommitResult } from "../interfaces/IFilePreCommitResult";
import { ListItemCreationData } from "../interfaces/ListItemCreationData";
import { logger } from "../../../utilities/logger";
import authModule from "../../../utilities/AuthModule";
import { ITessAccount, ITessAccountsResult, ITessEmailRequest, ITessEmailTaskResponse, ITessProjectFolders } from "./TessService";
import { ICompleteFileUploadRequest, IFileUploadRequest } from "../interfaces/IFileUploadRequest";

const handleError = (error: any) => {
    if (error?.response?.status === 401 || error?.response?.status === 403) {
        console.error(error?.response?.data);
    } else {
        logger(error?.response?.data, "ApiService.ts (handleError)");
        throw error;
    }
}

export const refreshJwt = async (accessToken: string) => {
    try {
        // Use default axios retry instance to skip access token refresh interceptor
        const result: IJwtAuthTokenResult = (await axiosRetryGet(
            `${process.env.API_URL}auth/RefreshJwt`,
            {
                headers: {
                    Authorization: `Bearer ${accessToken}`,
                    [apiHeaders.key.xDocubirdAuth]: apiHeaders.value.xDocubirdAuth
                }
            }
        ))?.data;
        return result;
    } catch (error) {
        handleError(error);
    }
    return null;
}

export const apiSearchDocuments = async (documentSearchRequest: IDocumentSearchRequest) => {
    try {
        const results: IDocumentSearchResult[] = (await axiosRetryPost("Search/Documents", documentSearchRequest, undefined, true, localApiInstance))?.data;
        return results ?? [];
    } catch (error) {
        handleError(error);
    }
    return [];
}

export const apiGetTenantInfo = async (tenantId?: string) => {
    try {
        const results: ITenantInfo = (await axiosRetryGet(`tenants/${tenantId}`, undefined, localApiInstance))?.data;
        return results ?? undefined;
    } catch (error) {
        handleError(error);
    }
    return undefined;
}

export const apiUpdateUserSettings = async (dbSettings: IUserSettings, accountId?: string) => {
    try {
        const results = (await axiosRetryPost(`User/UpdateUserSettings?accountId=${accountId}`, dbSettings, undefined, true, localApiInstance))?.data;
        return results ?? undefined;
    } catch (error) {
        handleError(error);
    }
    return undefined;
}

export const apiDownloadFileStream = async (downloadRequestPayload: IDownloadRequest) => {
    try {
        const fileBlob: Blob = (await axiosRetryPost("File/Download", downloadRequestPayload, {
            headers: {
                "Content-Type": "application/json"
            },
            responseType: "blob"
        }, true, localApiInstance))?.data;

        return fileBlob ?? undefined;
    } catch (error) {
        handleError(error);
    }
    return undefined;
}

export const apiCreateDocumentSet = async (documentSetCreationData: IDocumentSetCreationData): Promise<void> => {
    try {
        await axiosRetryPost("sharepoint/CreateDocumentSet", documentSetCreationData, undefined, true, localApiInstance);
    } catch (error) {
        handleError(error);
    }
}

export const apiGetCompressedPinnedData = async (accountId: string) => {
    try {
        const results: string = (await axiosRetryGet(`User/GetPinnedItems/${accountId}`, undefined, localApiInstance))?.data.value;
        return results ?? undefined;
    } catch (error) {
        handleError(error);
    }
    return undefined;
}

export const apiGetOldPinnedItems = async (accountId: string) => {
    try {
        const results: IStoreItem[] = (await axiosRetryGet(`User/GetOldPinnedItems/${accountId}`, undefined, localApiInstance))?.data.value;
        return results ?? [];
    } catch (error) {
        handleError(error);
    }
    return [];
}

export const apiSetPinnedItems = async (accountId: string, updatedPinnedItems: IStoreItem[]): Promise<void> => {
    try {
        axiosRetryPost(`User/SetPinnedItems/${accountId}`,
            { encryptedJsonContent: LZString.compressToBase64(JSON.stringify(updatedPinnedItems)) }, undefined, true, localApiInstance);
    } catch (error) {
        handleError(error);
    }
}

interface SetSuggestedItemsRequest {
    accountId: string;
    senders: string[];
    conversationId: string;
    encryptedJsonContent?: string;
}

export const apiSetSuggestedItems = async (accountId: string, senders: string[], conversationId: string, updatedtSuggestedItems: IStoreItem[]): Promise<void> => {
    try {
        const request: SetSuggestedItemsRequest = {
            accountId,
            senders,
            conversationId,
            encryptedJsonContent: LZString.compressToBase64(JSON.stringify(updatedtSuggestedItems))
        }

        axiosRetryPost(`User/SetSuggestedSaveLocations`, request, undefined, true, localApiInstance);
    } catch (error) {
        handleError(error);
    }
}

export const apiGetCompressedSuggestedData = async (accountId: string, senders: string[], conversationId: string) => {
    try {
        const request: SetSuggestedItemsRequest = {
            accountId,
            senders,
            conversationId
        };
        const results = (await axiosRetryPost(`User/GetSuggestedSaveLocations`, request, undefined, true, localApiInstance))?.data.value;
        return results ?? undefined;
    } catch (error) {
        handleError(error);
    }
    return undefined;
}

export const apiGetCompressedRecentData = async (accountId: string) => {
    try {
        const results: string = (await axiosRetryGet(`User/GetRecentItems/${accountId}`, undefined, localApiInstance))?.data.value;
        return results ?? undefined;
    } catch (error) {
        handleError(error);
    }
    return undefined;
}

export const apiGetOldRecentItems = async (accountId: string) => {
    try {
        const results: IStoreItem[] = (await axiosRetryGet(`User/GetOldRecentItems/${accountId}`, undefined, localApiInstance))?.data.value;
        return results ?? [];
    } catch (error) {
        handleError(error);
    }
    return [];
}

export const apiSetRecentItems = async (accountId: string, updatedRecentItems: IStoreItem[]): Promise<void> => {
    try {
        axiosRetryPost(`User/SetRecentItems/${accountId}`,
            { encryptedJsonContent: LZString.compressToBase64(JSON.stringify(updatedRecentItems.filter(item => !item.cannotBeRemoved))) }, undefined, true, localApiInstance);
    } catch (error) {
        handleError(error);
    }
}

export const apiGetCompressedEmailToMetadataMapping = async (accountId: string) => {
    try {
        const results: string = (await axiosRetryGet(`User/GetEmailToMetadataMapping/${accountId}`, undefined, localApiInstance))?.data?.value;
        return results ?? undefined;
    } catch (error) {
        handleError(error);
    }
    return undefined;
}

export const apiSetEmailToMetadataMapping = async (accountId: string, compressedData: string): Promise<void> => {
    try {
        await axiosRetryPost(`User/SetEmailToMetadataMapping/${accountId}`, { encryptedJsonContent: compressedData }, undefined, true, localApiInstance);
    } catch (error) {
        handleError(error);
    }
}

type DataToSend =
    | IFileSystemUploadData
    | ISharepointOfficeDataDto
    | IOneDriveOfficeDataDto
    | ISharepointDataDto
    | IOneDriveDataDto;

export const apiFileSystemCallDynamicEndpoint = async (
    endpoint: string,
    retryInternalServerError: boolean,
    dataToSend: DataToSend
): Promise<any> => {
    try {
        const results = (await axiosRetryPost(endpoint, dataToSend, undefined, retryInternalServerError, localApiInstance))?.data;
        return results ?? undefined;
    } catch (error) {
        handleError(error);
    }

    return undefined;
}

export const apiConvertFileToPdf = async (requestPayload: IDriveItemRequest) => {
    try {
        const results: AxiosResponse = await axiosRetryPost("File/ConvertToPdf", requestPayload, {
            responseType: "blob"
        }, true, localApiInstance);

        return results ?? undefined;
    } catch (error) {
        handleError(error);
    }

    return undefined;
}

export const apiGetAttachmentExtension = async (ewsUrl: string, ewsToken: string, attachmentIdList: string[]) => {
    try {
        const results = (await axiosRetryPost("sharepoint/GetAttachmentExtension", { ewsUrl, ewsToken, attachmentIdList }, undefined, false, localApiInstance))?.data;;

        return results ?? undefined;
    } catch (error) {
        handleError(error);
    }

    return undefined;
}

export const apiFilePreCommitCheck = async (request: IFilePreCommitRequest) => {
    try {
        const results: IFilePreCommitResult = (await axiosRetryPost("sharepoint/FilePreCommitCheck", request, undefined, true, localApiInstance))?.data;

        return results ?? undefined;
    } catch (error) {
        handleError(error);
    }

    return undefined;
}

export const apiFolderPreCommitCheck = async (request: FolderPreCommitRequest) => {
    try {
        const results = (await axiosRetryPost("sharepoint/FolderPreCommitCheck", request, undefined, true, localApiInstance))?.data;

        return results ?? undefined;
    } catch (error) {
        handleError(error);
    }

    return undefined;
}

export const apiUpdateItemMetadata = async (request: MetadataUpdateRequest) => {
    try {
        const results = (await axiosRetryPost("sharepoint/UpdateItemMetadata", request, undefined, true, localApiInstance))?.data;

        return results ?? undefined;
    } catch (error) {
        handleError(error);
    }

    return undefined;
}

export const apiBulkUpdateItemMetadata = async (request: BulkMetadataUpdateRequest) => {
    try {
        const results = (await axiosRetryPost("sharepoint/BulkUpdateItemMetadata", request, undefined, true, localApiInstance))?.data;
        return results ?? undefined;
    } catch (error) {
        handleError(error);
    }
    return undefined;
}

export const apiVerifyAndFireUpdateTrigger = async (request: UpdateTriggerRequest) => {
    try {
        const results = (await axiosRetryPost("sharepoint/VerifyAndFireUpdateTrigger", request, undefined, true, localApiInstance))?.data;
        return results ?? undefined;
    } catch (error) {
        handleError(error);
    }
    return undefined;
}

export const apiAddListItem = async (request: ListItemCreationData) => {
    try {
        const results = (await axiosRetryPost("sharepoint/AddListItem", request, undefined, true, localApiInstance))?.data;

        return results ?? undefined;
    } catch (error) {
        handleError(error);
    }

    return undefined;
}

export const apiUploadExchangeMailFolder = async (request: ExchangeMailFolderUploadRequest) => {
    try {
        const results = (await axiosRetryPost("sharepoint/UploadExchangeMailFolder", request, undefined, true, localApiInstance))?.data;

        return results ?? undefined;
    } catch (error) {
        handleError(error);
    }

    return undefined;
}

export const apiSilentTokenRequest = async (accountId: string) => {
    try {
        const results: IAuthTokenResultDto = (await axiosRetryGet(`Auth/SilentTokenRequest?accountId=${accountId}`, undefined, localApiInstance))?.data;

        return results ?? undefined;
    } catch (error) {
        handleError(error);
    }

    return undefined;
}

export const apiRefreshTokenRequest = async (account: IAuthAccount) => {
    try {
        const results: IAuthTokenResultDto = (await axiosRetryPost("Auth/RefreshTokenRequest", {
            accountId: account.accountId,
            tenantId: account.tenantId,
            sharePointBaseUrl: account.sharePointBaseUrl,
            oneDriveWebUrl: account.oneDriveWebUrl,
            displayName: account.displayName
        },
            undefined, true, localApiInstance))?.data;

        return results ?? undefined;
    } catch (error) {
        handleError(error);
    }

    return undefined;
}

export const apiAquireFlowToken = async (authState: IAuthTokenResultDto) => {
    try {
        const results: IAuthTokenResultDto = (await axiosRetryGet(`Auth/AquireFlowToken?accountId=${authState.account.accountId}`, undefined, localApiInstance))?.data;

        return results ?? undefined;
    } catch (error) {
        handleError(error);
    }

    return undefined;
}

export const apiAquireSharePointAdminToken = async (authState: IAuthTokenResultDto) => {
    try {
        let authUrl: URL = new URL(`${process.env.API_URL}Auth/AquireSharePointAdminToken`);
        authUrl.searchParams.append("accountId", authState.account?.accountId);
        authUrl.searchParams.append("sharePointBaseUrl", authState.account?.sharePointBaseUrl);

        const results: IAuthTokenResultDto = (await axiosRetryGet(authUrl.href, undefined, localApiInstance))?.data;

        return results ?? undefined;
    } catch (error) {
        handleError(error);
    }

    return undefined;
}

export const apiAquireMailSendToken = async (authState: IAuthTokenResultDto) => {
    try {
        let authUrl: URL = new URL(`${process.env.API_URL}Auth/AquireMailSendToken`);
        authUrl.searchParams.append("accountId", authState.account?.accountId);

        const results: IAuthTokenResultDto = (await axiosRetryGet(authUrl.href, undefined, localApiInstance))?.data;

        return results ?? undefined;
    } catch (error) {
        handleError(error);
    }

    return undefined;
}

export const apiAquireMailboxToken = async (authState: IAuthTokenResultDto) => {
    try {
        let authUrl: URL = new URL(`${process.env.API_URL}Auth/AquireMailboxToken`);
        authUrl.searchParams.append("accountId", authState.account?.accountId);

        const results: IAuthTokenResultDto = (await axiosRetryGet(authUrl.href, undefined, localApiInstance))?.data;

        return results ?? undefined;
    } catch (error) {
        handleError(error);
    }

    return undefined;
}

// export const apiTeamsToken = async (authState: IAuthTokenResultDto) => {
//     try {
//         let authUrl: URL = new URL(`${process.env.API_URL}Auth/AquireTeamsToken`);
//         authUrl.searchParams.append("accountId", authState.account?.accountId);

//         const results: IAuthTokenResultDto = (await axiosRetryGet(authUrl.href, undefined, localApiInstance))?.data;

//         return results ?? undefined;
//     } catch (error) {
//         handleError(error);
//     }

//     return undefined;
// }

export const apiCheckIsTessAuthenticated = async (authState: IAuthTokenResultDto): Promise<boolean> => {
    try {
        let authUrl: URL = new URL(`${process.env.API_URL}Tess/CheckIsTessAuthenticated`);
        authUrl.searchParams.append("tenantId", authState.account?.tenantId);
        authUrl.searchParams.append("accountId", authState.account?.uniqueId);

        return (await axiosRetryGet(authUrl.href, undefined, localApiInstance))?.data === true;
    } catch (error) {
        handleError(error);
    }
    return false;
}

export const apiGetTessAccounts = async (accountEmailAddresses?: string[]): Promise<ITessAccountsResult> => {
    try {
        const authState = authModule.getPersistedAuthState();

        const requestUrl: URL = new URL(`${process.env.API_URL}Tess/GetAccounts`);
        requestUrl.searchParams.append("tenantId", authState.account?.tenantId);
        requestUrl.searchParams.append("accountId", authState.account?.uniqueId);

        const hasAccountEmailAddresses: boolean = Array.isArray(accountEmailAddresses) && accountEmailAddresses.length > 0;
        if (hasAccountEmailAddresses) {
            requestUrl.searchParams.append("email", accountEmailAddresses.map(x => x?.trim()).join(";"));
        }

        const results: ITessAccount[] = (await axiosRetryGet(requestUrl.href, undefined, localApiInstance))?.data;
        if (Array.isArray(results) && results.length > 0) {
            return { allowLoadAllAccounts: hasAccountEmailAddresses, accounts: results };
        }
        else if (hasAccountEmailAddresses) {
            // If no accounts were found for the sender address, load all accounts for signed in user
            return await apiGetTessAccounts();
        }
    } catch (error) {
        handleError(error);
    }
    return { allowLoadAllAccounts: false, accounts: [] };
}

export const apiGetTessProjects = async (accountId: string): Promise<ITessProjectFolders> => {
    try {
        const authState = authModule.getPersistedAuthState();

        const requestUrl: URL = new URL(`${process.env.API_URL}Tess/GetProjects`);
        requestUrl.searchParams.append("tenantId", authState.account?.tenantId);
        requestUrl.searchParams.append("accountId", authState.account?.uniqueId);
        requestUrl.searchParams.append("tessAccountId", accountId);

        const result: ITessProjectFolders = (await axiosRetryGet(requestUrl.href, undefined, localApiInstance))?.data;
        return result ?? null;
    } catch (error) {
        handleError(error);
    }
    return null;
}

export const apiPostTessEmail = async (requestPayload: ITessEmailRequest): Promise<ITessEmailTaskResponse> => {
    try {
        const authState = authModule.getPersistedAuthState();

        const requestUrl: URL = new URL(`${process.env.API_URL}Tess/Email`);
        requestUrl.searchParams.append("tenantId", authState.account?.tenantId);
        requestUrl.searchParams.append("accountId", authState.account?.uniqueId);

        const result: ITessEmailTaskResponse = (await axiosRetryPost(requestUrl.href, requestPayload, undefined, false, localApiInstance))?.data;
        return result ?? null;
    } catch (error) {
        handleError(error);
    }
    return null;
}

export const apiCreateFolders = async (accessToken: string, siteUrl: string, parentUrl: string, listId: string, autoFolderCreation: boolean, folders: IFileSystemFolder[]) => {
    try {
        const results: IFileSystemFolder[] = (await axiosRetryPost("sharepoint/CreateFolders", { accessToken, siteUrl, parentUrl, listId, autoFolderCreation, folders }, undefined, true, localApiInstance))?.data as IFileSystemFolder[];
        return results ?? []
    } catch (error) {
        handleError(error);
    }

    return []
}

export const apiUpload = async (fileUploadRequest: IFileUploadRequest, config?: AxiosRequestConfig) => {
    try {
        const uploadUrl: URL = new URL(`${process.env.API_URL}upload`);
        uploadUrl.searchParams.append("accountId", fileUploadRequest.accountId);

        if (fileUploadRequest.uploadId) {
            uploadUrl.searchParams.append("uploadId", fileUploadRequest.uploadId);
            uploadUrl.searchParams.append("chunkIndex", (fileUploadRequest.chunkIndex ?? 0).toString());
        }

        if (fileUploadRequest.path) {
            uploadUrl.searchParams.append("path", fileUploadRequest.path);
        }

        await axiosRetryPost(uploadUrl.href, fileUploadRequest.formData, config, true, localApiInstance);
    } catch (error) {
        handleError(error);
    }
}

export const apiCompleteChunkUpload = async (completeUploadRequest: ICompleteFileUploadRequest) => {
    try {
        await axiosRetryPut("upload/complete", completeUploadRequest, localApiInstance);
    } catch (error) {
        handleError(error);
    }
}

export const apiClientError = async (clientError: any) => {
    try {
        // Use static axios instance as to not retry logging calls
        await axios.post(`${process.env.API_URL}Log/ClientError`, clientError, {
            headers: {
                Authorization: `Bearer ${authModule.getDocubirdTokenCached()}`,
                [apiHeaders.key.xDocubirdAuth]: apiHeaders.value.xDocubirdAuth
            }
        });
    } catch (error) {
        console.error(error);
    }
}

export const apiLog = async (message: string, account: IAuthAccount, type?: string) => {
    try {
        await axiosRetryPost("Log", {
            message: `${message} - ${account?.username} - ${navigator?.userAgent}`,
            level: type
        }, undefined, true, localApiInstance);
    } catch (error) {
        handleError(error);
    }
}

export const apiSavePayload = async (mailPayloadDto: IMailSettingsDto) => {
    try {
        await axiosRetryPost("SaveOnSend/SavePayload", mailPayloadDto, undefined, true, localApiInstance);
    } catch (error) {
        handleError(error);
    }
}

export const apiGetPayload = async (accountId: string) => {
    try {
        const results: string = (await axiosRetryGet(`SaveOnSend/GetPayload/${accountId}`, undefined, localApiInstance))?.data
        return results ?? ""
    } catch (error) {
        handleError(error);
    }

    return "";
}

export const apiGetUserLicense = async (userAccount: IAuthAccount) => {
    try {
        const results: UserLicenseTypeEnum = (await axiosRetryPost("User/GetUserLicense", userAccount, undefined, true, localApiInstance))?.data;
        return results ?? UserLicenseTypeEnum.Expired
    } catch (error) {
        handleError(error);
    }

    return UserLicenseTypeEnum.Expired;
}

export const apiGetUserProfile = async (userAccount: IAuthAccount, appContext: string) => {
    try {
        const results: IUserProfile = (await axiosRetryPost(`User/GetUserProfile?appContext=${appContext}`, userAccount, undefined, true, localApiInstance))?.data;
        return results ?? null
    } catch (error) {
        handleError(error);
    }

    return null;
}

export const apiLoginAuthGetRequest = async (authUrlGetRequest: URL) => {
    try {
        // Use default axios retry instance to skip access token refresh interceptor
        const results = (await axiosRetryGet(
            authUrlGetRequest.href,
            {
                headers: {
                    [apiHeaders.key.xDocubirdAuth]: apiHeaders.value.xDocubirdAuth
                }
            }
        ))?.data;
        return results ?? undefined;
    } catch (error) {
        handleError(error);
    }

    return undefined;
}

export const apiLoginCodeTokenRequest = async (codeTokenRequestUrl: URL) => {
    try {
        // Use default axios retry instance to skip access token refresh interceptor
        const results = (await axiosRetryGet(
            codeTokenRequestUrl.href,
            {
                headers: {
                    [apiHeaders.key.xDocubirdAuth]: apiHeaders.value.xDocubirdAuth
                }
            }
        ))?.data;
        return results ?? undefined;
    } catch (error) {
        handleError(error);
    }

    return undefined;
}

export const apiGetBackgroundTaskProgess = async (taskId: string) => {
    try {
        const results = (await axiosRetryGet(`BackgroundTasks/progress/${taskId}`, undefined, localApiInstance))?.data;
        return results ?? undefined;
    } catch (error) {
        handleError(error);
    }

    return undefined;
}

export const apiFileReplace = async (request: ReplaceFileRequest) => {
    try {
        const results = (await axiosRetryPost("file/Replace", request, undefined, true, localApiInstance))?.data;
        return results ?? undefined;
    } catch (error) {
        handleError(error);
    }

    return undefined;
}

export const apiFileCreateDocumentFromTemplate = async (request: CreateDocumentFromTemplateRequest) => {
    try {
        const results = (await axiosRetryPost("file/CreateDocumentFromTemplate", request, undefined, true, localApiInstance))?.data;
        return results ?? undefined;
    } catch (error) {
        handleError(error);
    }
    return undefined;
}

export const apiFileConvertAndSaveAsPDF = async (request: ConvertAndSaveAsPDFRequest) => {
    try {
        const results = (await axiosRetryPost("file/ConvertAndSaveAsPDF", request, undefined, true, localApiInstance))?.data;
        return results ?? undefined;
    } catch (error) {
        handleError(error);
    }

    return undefined;
}

export const apiGetHooksByType = async (request: IHookRequest): Promise<IHookResult[]> => {
    try {
        const results = (await axiosRetryGet(`hooks?tenantGuid=${request.TenantGUID}&hookTypeId=${request.HookTypeId}`, undefined, localApiInstance))?.data;
        return results ?? []
    } catch (error) {
        handleError(error);
    }

    return [];
}

export const apiRequestApproval = async (request: ApprovalRequest) => {
    try {
        const results = (await axiosRetryPost("hooks/request/approval", request, undefined, true, localApiInstance))?.data;
        return results ?? undefined;
    } catch (error) {
        return handleError(error);
    }
}

// export const apiRequestSigning = async (data: SignRequest) => {
//     try {
//         const results = (await axiosRetryPost("sign/create", data, undefined, true, localApiInstance))?.data;
//         return results ?? undefined;
//     } catch (error) {
//         handleError(error);
//     }
//     return undefined;
// }

export const apiGetAdditionalMailboxes = async (accountId: string) => {
    try {
        const results = (await axiosRetryGet(`user/GetAdditionalMailboxes/${accountId}`, undefined, localApiInstance))?.data;
        return results ?? []
    } catch (error) {
        handleError(error);
    }

    return [];
}

export const apiSetAdditionalMailboxes = async (accountId: string, additionalMailboxes: string[]) => {
    try {
        const results = (await axiosRetryPost(`user/SetAdditionalMailboxes/${accountId}`, additionalMailboxes, undefined, true, localApiInstance))?.data;
        return results ?? undefined;
    } catch (error) {
        handleError(error);
    }
    return undefined;
}

interface IRemoveProvisionedSitesRequest {
    accountId: string;
    siteIds: string[];
}

export const apiGetProvisionedSites = async (accountId: string) => {
    try {
        const results = (await axiosRetryGet(`Provisioning/GetProvisionedSites/${accountId}`, undefined, localApiInstance))?.data;
        return results ?? [];
    } catch (error) {
        handleError(error);
    }
    return [];
}

export const apiRemoveProvisionedSites = async (accountId: string, siteIdsToRemove: string[]) => {
    try {
        const data: IRemoveProvisionedSitesRequest = {
            accountId,
            siteIds: siteIdsToRemove
        }
        await axiosRetryPost("Provisioning/RemoveProvisionedSites", data, undefined, false, localApiInstance);
    } catch (error) {
        handleError(error);
    }
}